import {
  ExportCommentsToXlsButton_AnswerFragment,
  ExportCommentsToXlsButton_StakeFragment,
  StakesDoubleMaterialityComments_AnswerFragment,
  StakesDoubleMaterialityComments_StakeFragment,
} from '@/graphql/generated';
import { StakeTag } from '../../../../stake/StakeTag';
import { utils, writeFileXLSX } from 'xlsx';
import { TooltipWrapper } from '@/components/generic/TooltipWrapper';
import { DownloadIcon, QuoteIcon } from '@/components/icons';
import { useTranslation } from '@/hooks/useTranslation';

export function StakesDoubleMaterialityComments({
  stakes,
  answers,
}: {
  stakes: StakesDoubleMaterialityComments_StakeFragment[];
  answers: StakesDoubleMaterialityComments_AnswerFragment[];
}) {
  const { t } = useTranslation();
  // Simple list of answer.stakes.comment (no matrix)
  // Grouped by stake (stake -> array of comments)
  const comments: {
    stake: StakesDoubleMaterialityComments_StakeFragment;
    comments: string[];
  }[] = answers
    .flatMap((answer) => answer.stakes)
    .filter((stakeAnswer) => stakeAnswer?.comment)
    .reduce(
      (
        acc: {
          stake: StakesDoubleMaterialityComments_StakeFragment;
          comments: string[];
        }[],
        stakeAnswer,
      ) => {
        const stake = stakes.find((stake) => stake.id === stakeAnswer?.stakeId);
        if (stake) {
          const stakeComments = acc.find(
            (stakeComment) => stakeComment.stake.id === stake.id,
          );
          if (stakeComments) {
            stakeComments.comments.push(stakeAnswer?.comment || '');
          } else {
            acc.push({ stake, comments: [stakeAnswer?.comment || ''] });
          }
        }
        return acc;
      },
      [],
    );

  if (comments.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h6>{t('survey.responses.comments.title_free')}</h6>
        <ExportCommentsToXlsButton answers={answers} stakes={stakes} />
      </div>

      <div className="space-y-4">
        {comments.map((comment, index) => (
          <div key={index} className="space-y-2">
            <div className="flex">
              <StakeTag
                stake={comment.stake}
                disableDetailModalOpening={true}
              />
            </div>
            <div className="space-y-4">
              {comment.comments.map((comment, index) => (
                <div className="flex items-start gap-2" key={index}>
                  <QuoteIcon className="shrink-0 text-gray-300" />
                  <div className="bg-white p-2 sami-body-small border-l-2 border-gray-100">
                    {comment}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ExportCommentsToXlsButton({
  stakes,
  answers,
}: {
  stakes: ExportCommentsToXlsButton_StakeFragment[];
  answers: ExportCommentsToXlsButton_AnswerFragment[];
}) {
  const { t, translateProperty } = useTranslation();
  const exportToXls = () => {
    const worksheet = utils.json_to_sheet(
      answers
        .flatMap((answer) => answer.stakes)
        .filter((stakeAnswer) => stakeAnswer?.comment)
        .map((stakeAnswer) => {
          const stake = stakes.find(
            (stake) => stake.id === stakeAnswer?.stakeId,
          );
          const stakeName = stake ? translateProperty(stake, 'name') : '';
          return {
            Stake: stakeName,
            Commentaire: stakeAnswer?.comment,
          };
        }),
    );

    const workbook = utils.book_new();
    utils.book_append_sheet(
      workbook,
      worksheet,
      t('survey.responses.comments.export.sheet'),
    );
    writeFileXLSX(
      workbook,
      `${t('survey.responses.comments.export.filename')}.xlsx`,
      {
        compression: true,
      },
    );
  };

  return (
    <TooltipWrapper label={t('survey.responses.comments.export.label')}>
      <button className="tertiary" onClick={exportToXls}>
        <DownloadIcon />
      </button>
    </TooltipWrapper>
  );
}
